<template>
    <div class="component-card-carousel">
        <div class="carousel">
            <div
                ref="container"
                class="slides"
            >
                <div
                    v-for="(item, index) in data.items"
                    :key="index"
                    ref="elements"
                    class="slide"
                >
                    <CardCarouselCard
                        :data="item"
                        :is-selected="current === index"
                    />
                </div>
            </div>
        </div>

        <ButtonRound
            :disabled="isAtStart"
            class="button-round left"
            icon="angle-left"
            :aria-label="t('catalogue.carousel.button_prev')"
            @click="previous"
        />

        <ButtonRound
            :disabled="isAtEnd"
            class="button-round right"
            icon="angle-right"
            :aria-label="t('catalogue.carousel.button_next')"
            @click="next"
        />
    </div>

    <div class="dots">
        <div
            v-for="index in pageCount"
            :key="index"
            class="dot"
            :class="getClassDot(index)"
            @click.prevent.stop="goToPage(index)"
        ></div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ComponentCardCarouselFragment } from '@/graphql/api'

import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'
import { useCarouselDotsPage } from '@/components/layout/carousels/base/useCarouselDotsPage'

import CardCarouselCard from '@/components/cms/component/carousel/card/CardCarouselCard.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'

interface Props {
    data: ComponentCardCarouselFragment
    selectedSlide?: string | null
    hasEagerImage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    selectedSlide: null,
    hasEagerImage: false,
})

const { t } = useI18n()

const {
    previous,
    next,
    slideWidth,
    container,
    elements,
    current,
    isAtStart,
    isAtEnd,
} = useCarousel(computed(() => props.data.items.length))

const { pageCount, getClassDot, goToPage } = useCarouselDotsPage(
    container,
    slideWidth,
    props.data.items,
    current
)
</script>

<style lang="scss" scoped>
.dots {
    display: flex;
    justify-content: center;
    padding: 40px 0 20px;

    @include min-width(1024) {
        padding: 40px 0;
    }

    .dot {
        flex: 0 0 7px;
        height: 7px;
        background-color: $grey-200;
        margin: 4px;
        border-radius: 50%;
        cursor: pointer;
        transition: background-color 80ms ease-in-out;

        &.active {
            background-color: $blue;
        }
    }
}

.component-card-carousel {
    position: relative;
    margin: auto;
    z-index: 10;
    max-width: 1440px;

    .carousel {
        width: 100%;
        height: 450px;

        @media (hover: hover) {
            height: 330px;
        }

        .slides {
            position: relative;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-padding: 0 20px;
            padding: 0 20px;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            &::after {
                content: '';
                padding-right: 20px;

                @include min-width(1024) {
                    padding-right: 0;
                }
            }

            @include min-width(1360) {
                padding: 0;
                scroll-padding: 0;
            }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: start;
                flex: 1 0 100%;
                margin-right: 5px;

                @include min-width(640) {
                    flex: 1 0 50%;
                    scroll-snap-align: start;
                }

                @include min-width(1024) {
                    flex: 1 0 33.33%;
                }

                @include min-width(1140) {
                    max-width: 340px;
                    flex: 1 0 25%;
                }

                @include min-width(1360) {
                    max-width: 359px;
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 25px);

        &.disabled {
            display: none;
        }

        &.left {
            left: 15px;
        }

        &.right {
            right: 15px;
        }

        @include min-width(1600) {
            &.left {
                left: -20px;
            }

            &.right {
                right: -20px;
            }
        }
    }
}
</style>
